import { Activities } from "../../../../types/apiTypes";
import { CircularProgress, Stack, Typography } from "@mui/material";
import { useAppDispatch, useGetStatusFromSSE } from "../../../../hooks";
import { useEffect, useState } from "react";
import {
  accountsApi,
  campaignsApi,
  useAnalyseMutation,
  useFindExternalDataRetryMutation,
  useGenerateAccountsPlanListMutation,
  useGeneratePlaybookForMembersMutation,
} from "../../../../api";
import { ActivitiesStatus, NotificationTypes } from "../../../../types/enums";
import { Link } from "react-router-dom";
import { setIsFetchTableMembers } from "../../../../features/companySlice";
import { notificationLinkMapper } from "../../../../utils/notificationLinkMapper";

export const InfoItem = ({
  company,
  campaign_id,
  isScraping,
  type,
  isEditor,
}: {
  company: Activities;
  campaign_id: string;
  isScraping: boolean;
  isEditor?: boolean;
  type: NotificationTypes;
}) => {
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [currentProgress, setCurrentProgress] = useState<number>(
    company.progress,
  );
  const [currentStatus, setCurrentStatus] = useState<ActivitiesStatus>(
    company.status,
  );

  const { analysis_status, scraping_status } = useGetStatusFromSSE(campaign_id);

  const [analyse, { isLoading: isAnalyze }] = useAnalyseMutation();
  const [findExternalDataRetry, { isLoading: isFindExternalData }] =
    useFindExternalDataRetryMutation();
  const [generateAccountsPlanList, { isLoading: isGeneratingAccountsPlan }] =
    useGenerateAccountsPlanListMutation();
  const [generatePlaybookForMembers, { isLoading: isGeneratingPlaybook }] =
    useGeneratePlaybookForMembersMutation();

  useEffect(() => {
    if (analysis_status && analysis_status?.account_id === company.id) {
      setCurrentProgress(analysis_status.progress);
    }
  }, [analysis_status]);

  useEffect(() => {
    if (scraping_status && scraping_status?.account_id === company.id) {
      const status = scraping_status.status as any;
      setCurrentStatus(status);
    }
  }, [scraping_status]);

  useEffect(() => {
    (isAnalyze ||
      isFindExternalData ||
      isGeneratingAccountsPlan ||
      isGeneratingPlaybook) &&
      setIsLoading(true);
  }, [
    isAnalyze,
    isFindExternalData,
    isGeneratingAccountsPlan,
    isGeneratingPlaybook,
  ]);

  const tryAgainHandler = async (account_ids: string[]) => {
    switch (type) {
      case NotificationTypes.scraping:
        await findExternalDataRetry({ campaign_id, account_ids });
        break;

      case NotificationTypes.analysis:
        await analyse({ account_ids, campaign_id });
        break;

      case NotificationTypes.account_plan_generation:
        await generateAccountsPlanList({ campaign_id, account_ids })
          .unwrap()
          .then(() => {
            dispatch(accountsApi.util.invalidateTags(["list-view"]));
            dispatch(campaignsApi.util.invalidateTags(["summary"]));
          });
        break;

      case NotificationTypes.sdr_playbook_generation:
        await generatePlaybookForMembers({
          campaign_id,
          ids: account_ids,
          include_all: false,
        })
          .unwrap()
          .then(() => {
            dispatch(accountsApi.util.invalidateTags(["list-view"]));
            dispatch(
              campaignsApi.util.invalidateTags([
                "summary",
                "company",
                "role-members",
              ]),
            );
            dispatch(setIsFetchTableMembers(true));
          });
        break;
    }

    dispatch(campaignsApi.util.invalidateTags(["activities"]));
  };

  return (
    <Stack
      flexDirection="row"
      gap="8px"
      alignItems="center"
      justifyContent="space-between"
    >
      <Link
        onClick={(e) => {
          if (
            [ActivitiesStatus.failed, ActivitiesStatus.in_progress].includes(
              company.status,
            ) &&
            [
              NotificationTypes.sdr_playbook_generation,
              NotificationTypes.account_plan_generation,
            ].includes(type)
          )
            e.preventDefault();
        }}
        to={notificationLinkMapper(type, campaign_id, company.id)}
        style={{ textDecoration: "none", color: "#1F2932" }}
      >
        <Typography variant="body1" color="#1F2932">
          {company.name}
        </Typography>
      </Link>
      {company.status === ActivitiesStatus.failed && isEditor && (
        <Stack sx={{ marginLeft: "auto" }}>
          {isLoading ? (
            <CircularProgress size={20} sx={{ color: "#3279B3" }} />
          ) : (
            <span
              onClick={() => tryAgainHandler([company.id])}
              style={{
                cursor: "pointer",
                color: "#1B3967",
                textDecoration: "underline",
                textWrap: "nowrap",
              }}
            >
              Try Again
            </span>
          )}
        </Stack>
      )}
      {company.status === ActivitiesStatus.in_progress && (
        <Stack sx={{ marginLeft: "auto" }}>
          {isScraping ? (
            <>
              {[ActivitiesStatus.in_progress, ActivitiesStatus.queued].includes(
                currentStatus,
              ) ? (
                <CircularProgress size={20} sx={{ color: "#3279B3" }} />
              ) : (
                <span
                  style={{
                    color: "#1F2932",
                  }}
                >
                  {currentStatus === ActivitiesStatus.completed
                    ? "Ready"
                    : "No contacts found"}
                </span>
              )}
            </>
          ) : (
            <>
              {!currentProgress ? (
                <CircularProgress size={20} sx={{ color: "#3279B3" }} />
              ) : (
                <span
                  style={{
                    color: "#1F2932",
                  }}
                >
                  {`${currentProgress}%`}
                </span>
              )}
            </>
          )}
        </Stack>
      )}
    </Stack>
  );
};
